/**
 * ScriptStore — Postgres metadata + on-disk body for script recipes.
 *
 * Split of responsibilities:
 *   - METADATA  → injected `ScriptRecipeStore` (Postgres FTS + atomic counters),
 *                 resolved from `collections['script-recipes']`.
 *   - BODY      → scripts-store/<fileBase>.ts, editable in your IDE. Written
 *                 atomically (temp + rename); `bodyHash` (sha256) detects edits.
 *
 * The old "read every file every turn + send the whole catalog to the LLM"
 * matcher is gone — matching is `store.search(prompt)` (FTS shortlist). The
 * draft/verified filename dance is gone too: `status` is a DB column and the
 * file keeps a stable `<fileBase>.ts` name across promotion.
 *
 * When no metadata store is injected, the store degrades to a safe no-op
 * (count 0 → script flow disabled) instead of crashing.
 *
 * See backend/docs/SCRIPT-FLOW-SCALING-ISSUES.md.
 */

import type { ScriptRecipe, ScriptParameter, ScriptComponentSpec } from './types';
import type { ScriptRecipeStore, ScriptRecipeMetaRow } from './script-metadata-store';
import { resolveScriptRecipeStore } from './script-metadata-store';
import { logger } from '../../utils/logger';
import * as fs from 'fs';
import * as path from 'path';
import { createHash, randomBytes } from 'crypto';

const DEFAULT_STORE_DIR = 'scripts-store';

export interface SaveDraftInput {
	/** Reuse an existing draft (retry); omit to mint a new one. */
	recipeId?: string;
	/** Per-turn unique suffix, stable across retries within the turn. */
	turnId: string;
	name: string;
	intentDescription: string;
	tags: string[];
	parameters: ScriptParameter[];
	scriptBody: string;
	createdFrom: string;
}

export interface PromoteToVerifiedInput {
	sourceIds: string[];
	tables: string[];
	parentId?: string;
	forkDepth?: number;
	forkReason?: string;
	components?: ScriptComponentSpec[];
}

export interface ScriptStoreOptions {
	/** Explicit metadata store, or resolved from `collections['script-recipes']`. */
	store?: ScriptRecipeStore | null;
	collections?: any;
	/** Body directory (defaults to <cwd>/scripts-store). */
	baseDir?: string;
	/** Project scope stamped on every row. */
	projectId?: string;
}

/**
 * Normalize a scriptBody into the on-disk form (strip a leading comment block,
 * ensure `export async function getData`). Exported for MainAgent.
 */
export function normalizeScriptBody(scriptBody: string): string {
	let i = 0;
	const n = scriptBody.length;
	while (i < n) {
		while (i < n && /\s/.test(scriptBody[i])) i++;
		if (i >= n) break;
		if (scriptBody.startsWith('//', i)) {
			const nl = scriptBody.indexOf('\n', i);
			i = nl === -1 ? n : nl + 1;
			continue;
		}
		if (scriptBody.startsWith('/*', i)) {
			const end = scriptBody.indexOf('*/', i + 2);
			i = end === -1 ? n : end + 2;
			continue;
		}
		break;
	}
	let cleanBody = scriptBody.slice(i);

	// The model sometimes wraps the body in a markdown code fence (```ts … ```),
	// or leaves a stray trailing closer after the final `}` (a lone ` or `);`) when
	// it loses backtick balance across nested template literals. A single unmatched
	// ` makes esbuild fail with "Unterminated string literal" and kills the whole
	// script. Strip leading/trailing fences, then — only when the backtick count is
	// odd (genuinely unbalanced) — drop the stray trailing backtick plus any trailing
	// ) / ; / whitespace. The odd-count guard means valid scripts are never touched.
	cleanBody = cleanBody.replace(/^\s*```[a-zA-Z]*[ \t]*\r?\n/, '');
	cleanBody = cleanBody.replace(/\r?\n?[ \t]*```[ \t]*$/, '');
	if (((cleanBody.match(/(?<!\\)`/g) || []).length) % 2 === 1) {
		cleanBody = cleanBody.replace(/`[\s);]*$/, '');
	}

	// Ensure `getData` is exported — the bootstrap loads it via `mod.getData`.
	// The model doesn't always emit `export`, and `getData` is NOT always the
	// first declaration (imports, helper functions, or a `const getData = ...`
	// arrow form can precede it). If it's already exported in any form, leave
	// the body untouched (it may legitimately have other declarations). Only
	// when no export of `getData` exists do we add one, matching the
	// declaration wherever it sits.
	const alreadyExported =
		/\bexport\s+(async\s+)?function\s+getData\b/.test(cleanBody) ||
		/\bexport\s+(const|let|var)\s+getData\b/.test(cleanBody) ||
		/\bexport\s+default\b/.test(cleanBody) ||
		/\bexport\s*\{[^}]*\bgetData\b/.test(cleanBody);

	if (!alreadyExported) {
		const fnDecl = /(^|\n)([ \t]*)(async\s+)?function\s+getData\b/;
		const varDecl = /(^|\n)([ \t]*)(const|let|var)\s+getData\b/;
		if (fnDecl.test(cleanBody)) {
			cleanBody = cleanBody.replace(fnDecl, (_m, lead, ws, asyncKw) =>
				`${lead}${ws}export ${asyncKw || ''}function getData`,
			);
		} else if (varDecl.test(cleanBody)) {
			cleanBody = cleanBody.replace(varDecl, (_m, lead, ws, kw) => `${lead}${ws}export ${kw} getData`);
		} else if (/\bgetData\b/.test(cleanBody)) {
			// Identifier is referenced but in a form we couldn't rewrite — export
			// it explicitly so the bootstrap can still pick it up.
			cleanBody = `${cleanBody.replace(/\s*$/, '')}\n\nexport { getData };\n`;
		}
	}

	// \d, \w, \s etc. inside JS template literals lose their backslash at runtime
	// (unrecognized escape sequences are silently dropped). Double them so the
	// on-disk .ts file produces the correct regex string when tsx executes it.
	// Only targets regex metacharacters — valid JS escapes (\n, \t, \r, etc.) are left alone.
	cleanBody = cleanBody.replace(/(?<!\\)\\([dwsDWS])/g, '\\\\$1');

	return cleanBody;
}

export class ScriptStore {
	private store: ScriptRecipeStore | null;
	private storeDir: string;
	private projectId?: string;

	constructor(opts?: ScriptStoreOptions) {
		this.store = opts?.store ?? resolveScriptRecipeStore(opts?.collections) ?? null;
		this.storeDir = opts?.baseDir || path.join(process.cwd(), DEFAULT_STORE_DIR);
		this.projectId = opts?.projectId;
		if (!this.store) {
			logger.warn('[ScriptStore] No script-recipes metadata store injected — script reuse disabled this run.');
		}
	}

	/** Whether a metadata store is wired (matcher / authoring are gated on this). */
	hasStore(): boolean {
		return this.store !== null;
	}

	// ============================================
	// Read
	// ============================================

	/** Number of healthy verified recipes (gates the script-matching path). */
	async count(): Promise<number> {
		if (!this.store) return 0;
		try {
			return await this.store.count({ projectId: this.projectId });
		} catch (err) {
			logger.warn(`[ScriptStore] count failed: ${err}`);
			return 0;
		}
	}

	/**
	 * FTS shortlist for the matcher (metadata only — bodies are loaded lazily by
	 * `get()` once the LLM picks one). Returns verified, healthy recipes ranked
	 * by relevance.
	 */
	async search(prompt: string, limit?: number): Promise<ScriptRecipe[]> {
		if (!this.store) return [];
		try {
			const rows = await this.store.search({ prompt, projectId: this.projectId, limit });
			return rows.map(r => this.rowToRecipe(r, '')); // body omitted for the catalog
		} catch (err) {
			logger.warn(`[ScriptStore] search failed: ${err}`);
			return [];
		}
	}

	/** Fetch one recipe by id with its body loaded from disk. */
	async get(id: string): Promise<ScriptRecipe | null> {
		if (!this.store) return null;
		try {
			const row = await this.store.getById(id);
			if (!row) return null;
			const body = this.readBody(row.fileBase);
			return this.rowToRecipe(row, body);
		} catch (err) {
			logger.warn(`[ScriptStore] get(${id}) failed: ${err}`);
			return null;
		}
	}

	// ============================================
	// Write
	// ============================================

	/** Create or update a recipe (metadata upsert + body write when changed). */
	async save(recipe: ScriptRecipe): Promise<void> {
		if (!this.store) return;
		try {
			if (!recipe.fileBase) {
				recipe.fileBase = await this.computeFileBase(recipe.name, recipe.id);
			}
			if (recipe.scriptBody) {
				const normalized = normalizeScriptBody(recipe.scriptBody);
				const hash = this.hash(normalized);
				if (hash !== recipe.bodyHash) {
					this.writeBody(recipe.fileBase, normalized);
					recipe.bodyHash = hash;
				}
			}
			recipe.updatedAt = new Date().toISOString();
			await this.store.upsert(this.recipeToRow(recipe));
			logger.info(`[ScriptStore] Saved "${recipe.name}" (${recipe.id})`);
		} catch (err) {
			logger.warn(`[ScriptStore] save("${recipe.name}") failed: ${err}`);
		}
	}

	/**
	 * Persist (or update) a draft. Within a turn, retries that pass the same
	 * `recipeId` overwrite the same row + file; a fresh `recipeId` mints a new
	 * draft. The body is visible at scripts-store/<fileBase>.ts immediately.
	 */
	async saveDraft(input: SaveDraftInput): Promise<ScriptRecipe> {
		if (!this.store) {
			throw new Error('[ScriptStore] saveDraft called with no metadata store injected');
		}
		const now = new Date().toISOString();
		const existing = input.recipeId ? await this.store.getById(input.recipeId) : null;
		const reuse = existing && existing.status === 'draft' && existing.turnId === input.turnId;

		const id = reuse ? existing!.id : `script_${Date.now()}_${randomBytes(3).toString('hex')}`;
		const fileBase = reuse ? existing!.fileBase : await this.computeFileBase(input.name, id);

		const normalized = normalizeScriptBody(input.scriptBody);
		const bodyHash = this.hash(normalized);
		this.writeBody(fileBase, normalized);

		const recipe: ScriptRecipe = {
			id,
			version: existing?.version ?? 1,
			name: input.name,
			intentDescription: input.intentDescription,
			tags: input.tags,
			sourceIds: existing?.sourceIds ?? [],
			tables: existing?.tables ?? [],
			parameters: input.parameters,
			scriptBody: normalized,
			fileBase,
			bodyHash,
			projectId: this.projectId,
			successCount: existing?.successCount ?? 0,
			failureCount: existing?.failureCount ?? 0,
			lastUsed: now,
			createdFrom: input.createdFrom,
			createdAt: existing?.createdAt ?? now,
			updatedAt: now,
			forkDepth: existing?.forkDepth ?? 0,
			status: 'draft',
			turnId: input.turnId,
		};

		await this.store.upsert(this.recipeToRow(recipe));
		logger.info(`[ScriptStore] ${reuse ? 'Updated' : 'Saved'} draft "${recipe.name}" (${id}) at ${this.getScriptPath(recipe)}`);
		return recipe;
	}

	/** Stamp a draft's last execution error (metadata only). */
	async recordDraftError(
		recipeId: string,
		err: { phase: 'compile' | 'runtime' | 'timeout' | 'ipc'; message: string; attempt: number },
	): Promise<void> {
		if (!this.store) return;
		try {
			await this.store.recordDraftError(recipeId, { ...err, at: new Date().toISOString() });
		} catch (e) {
			logger.warn(`[ScriptStore] recordDraftError failed: ${e}`);
		}
	}

	/**
	 * Promote a successfully-executed draft into a verified script.
	 * The on-disk body already exists at <fileBase>.ts (written at write_script
	 * time) and keeps its name — only the DB row flips status + provenance.
	 */
	async promoteToVerified(recipeId: string, input: PromoteToVerifiedInput): Promise<ScriptRecipe | null> {
		if (!this.store) return null;
		try {
			const row = await this.store.promote(recipeId, {
				sourceIds: input.sourceIds,
				tables: input.tables,
				parentId: input.parentId,
				forkDepth: input.forkDepth,
				forkReason: input.forkReason,
				components: input.components,
			});
			if (!row) {
				logger.warn(`[ScriptStore] promoteToVerified: recipe "${recipeId}" not found`);
				return null;
			}
			logger.info(`[ScriptStore] Promoted "${row.name}" (${recipeId}) → verified`);
			return this.rowToRecipe(row, this.readBody(row.fileBase));
		} catch (err) {
			logger.warn(`[ScriptStore] promoteToVerified failed: ${err}`);
			return null;
		}
	}

	/**
	 * Drop a draft (row + body file). MainAgent calls this at end-of-turn when a
	 * draft was authored but never verified — failed drafts are never matched, so
	 * deleting them immediately avoids unbounded accumulation (#5). No-op if the
	 * recipe isn't a draft (so a promoted/verified script is never removed here).
	 */
	async discardDraft(recipeId: string): Promise<void> {
		await this.removeById(recipeId, 'draft');
	}

	/** Delete a recipe (row + body file). */
	async delete(id: string): Promise<void> {
		await this.removeById(id);
	}

	/** Record a successful execution (atomic counter bump). */
	async recordSuccess(id: string): Promise<void> {
		if (!this.store) return;
		try {
			await this.store.updateStats(id, { successDelta: 1, lastUsed: new Date().toISOString() });
		} catch (err) {
			logger.warn(`[ScriptStore] recordSuccess failed: ${err}`);
		}
	}

	/** Record a failed execution (atomic counter bump). */
	async recordFailure(id: string): Promise<void> {
		if (!this.store) return;
		try {
			await this.store.updateStats(id, { failureDelta: 1, lastUsed: new Date().toISOString() });
		} catch (err) {
			logger.warn(`[ScriptStore] recordFailure failed: ${err}`);
		}
	}

	// ============================================
	// Paths (sync — body lives on disk)
	// ============================================

	/** Absolute path to the .ts body for a recipe (used by the runner/MainAgent). */
	getScriptPath(recipe: ScriptRecipe): string {
		const base = recipe.fileBase || this.toSlug(recipe.name);
		return path.join(this.storeDir, `${base}.ts`);
	}

	// ============================================
	// Internals
	// ============================================

	private async removeById(id: string, requireStatus?: 'draft'): Promise<void> {
		if (!this.store) return;
		try {
			const row = await this.store.getById(id);
			if (!row) return;
			if (requireStatus && row.status !== requireStatus) return;
			await this.store.remove(id);
			this.unlinkBody(row.fileBase);
			logger.info(`[ScriptStore] Removed "${row.name}" (${id})`);
		} catch (err) {
			logger.warn(`[ScriptStore] remove(${id}) failed: ${err}`);
		}
	}

	private rowToRecipe(row: ScriptRecipeMetaRow, body: string): ScriptRecipe {
		return {
			id: row.id,
			version: row.version ?? 1,
			name: row.name,
			intentDescription: row.intentDescription,
			tags: row.tags ?? [],
			sourceIds: row.sourceIds ?? [],
			tables: row.tables ?? [],
			parameters: (row.parameters as ScriptParameter[]) ?? [],
			scriptBody: body,
			fileBase: row.fileBase,
			bodyHash: row.bodyHash ?? undefined,
			projectId: row.projectId ?? undefined,
			successCount: row.successCount ?? 0,
			failureCount: row.failureCount ?? 0,
			lastUsed: row.lastUsed ?? new Date().toISOString(),
			createdFrom: row.createdFrom ?? '',
			createdAt: row.createdAt ?? new Date().toISOString(),
			updatedAt: row.updatedAt ?? new Date().toISOString(),
			parentId: row.parentId ?? undefined,
			forkDepth: typeof row.forkDepth === 'number' ? row.forkDepth : 0,
			forkReason: row.forkReason ?? undefined,
			components: (row.components as ScriptComponentSpec[]) ?? undefined,
			status: (row.status as 'draft' | 'verified') ?? 'verified',
			turnId: row.turnId ?? undefined,
			lastError: row.lastError ?? undefined,
		};
	}

	private recipeToRow(recipe: ScriptRecipe): ScriptRecipeMetaRow {
		return {
			id: recipe.id,
			projectId: recipe.projectId ?? this.projectId ?? null,
			version: recipe.version ?? 1,
			name: recipe.name,
			intentDescription: recipe.intentDescription,
			tags: recipe.tags ?? null,
			createdFrom: recipe.createdFrom ?? null,
			sourceIds: recipe.sourceIds ?? null,
			tables: recipe.tables ?? null,
			parameters: recipe.parameters ?? null,
			components: recipe.components ?? null,
			fileBase: recipe.fileBase || this.toSlug(recipe.name),
			bodyHash: recipe.bodyHash ?? null,
			successCount: recipe.successCount ?? 0,
			failureCount: recipe.failureCount ?? 0,
			lastUsed: recipe.lastUsed ?? null,
			parentId: recipe.parentId ?? null,
			forkDepth: typeof recipe.forkDepth === 'number' ? recipe.forkDepth : 0,
			forkReason: recipe.forkReason ?? null,
			status: recipe.status ?? 'verified',
			turnId: recipe.turnId ?? null,
			lastError: recipe.lastError ?? null,
		};
	}

	/** slug of name, with a short id suffix when the bare slug is already taken. */
	private async computeFileBase(name: string, id: string): Promise<string> {
		const slug = this.toSlug(name);
		if (this.store) {
			try {
				const taken = await this.store.fileBaseTaken(slug, id, this.projectId);
				if (taken) return `${slug}-${id.slice(-6)}`;
			} catch { /* fall through to bare slug */ }
		}
		return slug;
	}

	private toSlug(name: string): string {
		return name
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '')
			|| 'unnamed-script';
	}

	private hash(body: string): string {
		return createHash('sha256').update(body).digest('hex');
	}

	private bodyPath(fileBase: string): string {
		return path.join(this.storeDir, `${fileBase}.ts`);
	}

	private readBody(fileBase: string): string {
		try {
			return fs.readFileSync(this.bodyPath(fileBase), 'utf-8');
		} catch (err) {
			logger.warn(`[ScriptStore] body file ${fileBase}.ts missing/unreadable: ${err}`);
			return '';
		}
	}

	/** Atomic body write (temp + rename) so concurrent reads never see a partial file. */
	private writeBody(fileBase: string, normalizedBody: string): void {
		if (!fs.existsSync(this.storeDir)) fs.mkdirSync(this.storeDir, { recursive: true });
		const finalPath = this.bodyPath(fileBase);
		const tmpPath = `${finalPath}.tmp-${randomBytes(4).toString('hex')}`;
		fs.writeFileSync(tmpPath, normalizedBody);
		fs.renameSync(tmpPath, finalPath);
	}

	private unlinkBody(fileBase: string): void {
		try {
			const p = this.bodyPath(fileBase);
			if (fs.existsSync(p)) fs.unlinkSync(p);
		} catch (err) {
			logger.warn(`[ScriptStore] failed to delete body ${fileBase}.ts: ${err}`);
		}
	}
}
